<script setup lang="ts">
import { CalendarGroupService } from '@/class/services/CalendarGroupService'
import AppState, { calendarOptions } from '@/states/app'
import {
  UIForm,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIModal,
  UIModalContent,
  UIModalFooter,
  UIModalHeader,
  UISelect,
  useNotification,
} from '@gohighlevel/ghl-ui'
import { isEqual } from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConflictingCalendars } from '../../composables/useConflictingCalendars'
import Calendar from '../../models/calendar'
import ConflictingCalendarsModal from './Groups/ConflictingCalendarsModal.vue'

const { t } = useI18n()

const emit = defineEmits([
  'closeModal',
  'createdRecord',
  'uploadCSV',
  'update:show',
])

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  editPayload: {
    type: Object,
    default: null,
  },
})

const isSlugValidating = ref(false)

const teamCalendars = computed(() => {
  const calendars = (AppState?.globalCalendars?.calendars ||
    []) as unknown as Calendar[]

  return calendars.filter(
    calendar =>
      calendar.providerId && calendar.providerId === props.editPayload?.id
  )
})

watch(
  () => props.show,
  newVal => {
    saving.value = false
    if (newVal) {
      formValue.value = {
        name: props.editPayload?.name,
        description: props.editPayload?.description,
        slug: props.editPayload?.slug,
        widgetType: props.editPayload
          ? props.editPayload?.widgetType
            ? props.editPayload?.widgetType
            : 'classic'
          : 'neo',
      }
      selectedCalendars.value =
        teamCalendars?.value?.map(calendar => calendar.id) || []
      previousSelectedCalendars.value = selectedCalendars.value
    }
  }
)

const rules = {
  name: {
    required: true,
    message: t('validation_messages.calendar_group_name_required'),
    trigger: ['input', 'blur'],
  },
  description: {
    required: false,
    message: t('validation_messages.calendar_group_description_required'),
    trigger: ['input', 'blur'],
  },
  slug: {
    required: true,
    // trigger: [],
    asyncValidator(rule, value) {
      if (props.editPayload?.slug && value === props.editPayload?.slug) {
        return Promise.resolve()
      }
      return new Promise(async (resolve, reject) => {
        // @todo - add slug validating API and fail validation if not unique
        isSlugValidating.value = true
        try {
          if (!value?.trim()) {
            reject(t('validation_messages.calendar_group_slug_required'))
          }
          const { data } = await CalendarGroupService.validateSlug(
            AppState.locationId,
            value.trim()
          )
          if (!data?.available) {
            return reject(new Error('This slug is already taken'))
          }
        } catch (err) {
          return reject(err)
        } finally {
          isSlugValidating.value = false
        }

        resolve(true)
      })
    },
  },
}

const formValue = ref({
  name: '',
  description: '',
  slug: '',
  widgetType: 'neo',
})

const selectedCalendars = ref([])
const previousSelectedCalendars = ref([])
const showConflictingCalendars = ref(false)

const saving = ref(false)

const { getConflictingCalendars, getConflictingCalendarsTable } =
  useConflictingCalendars()

async function createCalendarGroup(values) {
  values.description = values.description ?? ''
  try {
    let groupData: any
    let isNew = false
    let fetchCalendars = false
    saving.value = true
    if (props.editPayload) {
      const areCalendarsChanged = !isEqual(
        previousSelectedCalendars.value,
        selectedCalendars.value
      )
      fetchCalendars = areCalendarsChanged
      const { data } = await CalendarGroupService.edit(props.editPayload.id, {
        ...values,
        ...(areCalendarsChanged && {
          selectedCalendarIds: selectedCalendars.value,
        }),
      })
      groupData = data?.group
    } else {
      const { data } = await CalendarGroupService.create({
        ...values,
        locationId: AppState.locationId,
      })
      groupData = data?.group
      isNew = true
    }

    saving.value = false
    emit('createdRecord', groupData, isNew, fetchCalendars)
  } catch (err) {
    saving.value = false
    useNotification().error({
      duration: 1.5 * 1000,
      content: 'Something went wrong. Please try again.',
    })
  }
}

const formRef = ref()

function handleValidateClick() {
  formRef.value
    .getForm()
    .validate()
    .then(async value => {
      if (conflictingCalendarsTable.value.length > 0) {
        showConflictingCalendars.value = true
      } else {
        await createCalendarGroup(formValue.value)
        Object.keys(formValue.value).forEach(key => {
          formValue.value[key] = ''
        })
        selectedCalendars.value = []
      }
    })
    .catch(e => console.error('error'))
}

watch(
  () => formValue.value.slug,
  () => {
    if (formValue.value.slug) {
      formValue.value.slug = formValue.value.slug
        .replace(/[^a-zA-Z0-9-]/g, '')
        .toLowerCase()
    }
  }
)

const groupTemplateOptions = computed(() => {
  return [
    {
      value: 'neo',
      label: 'Neo',
    },
    {
      value: 'classic',
      label: 'Classic',
    },
  ]
})

const conflictingCalendars = computed(() => {
  return getConflictingCalendars(
    selectedCalendars.value,
    props.editPayload?.id || ''
  )
})

const conflictingCalendarsTable = computed(() => {
  return getConflictingCalendarsTable(conflictingCalendars.value)
})

const onCancelConflictModal = () => {
  showConflictingCalendars.value = false
}

const onConfirmConflictModal = async () => {
  showConflictingCalendars.value = false
  formRef.value
    .getForm()
    .validate()
    .then(async value => {
      await createCalendarGroup({
        ...formValue.value,
        selectedCalendarIds: selectedCalendars.value,
      })
      Object.keys(formValue.value).forEach(key => {
        formValue.value[key] = ''
      })
      selectedCalendars.value = []
    })
    .catch(e => console.error('error'))
}
</script>

<template>
  <div>
    <UIModal
      :show="show"
      :width="650"
      @update:show="val => $emit('update:show', val)"
    >
      <template #header>
        <UIModalHeader
          id="calendarGroupCreateModalHeader"
          type="success"
          :title="
            props.editPayload
              ? $t('settings.calendar.edit_group')
              : $t('settings.calendar.add_new_calendar_group')
          "
          @close="$emit('closeModal')"
        />
      </template>
      <UIForm
        id="calendarGroupCreateForm"
        ref="formRef"
        :model="formValue"
        :rules="rules"
        class="h-full w-full max-w-7xl sm:max-w-7xl"
      >
        <p class="m-0 p-0 text-sm">
          {{ $t('settings.calendar.calendar_group_description') }}
        </p>

        <UIModalContent>
          <p class="w-full border border-solid border-gray-200" />

          <div class="mt-6 grid">
            <UIFormItem :label="$t('settings.calendar.group_name')" path="name">
              <UIInput
                id="calendarProviderName"
                v-model="formValue.name"
                :placeholder="$t('settings.calendar.group_name')"
              />
            </UIFormItem>

            <UIFormItem
              :label="$t('settings.calendar.group_description')"
              path="description"
              :show-feedback="false"
            >
              <UIInput
                id="calendarProviderDescription"
                v-model="formValue.description"
                type="textarea"
                :placeholder="$t('settings.calendar.group_description')"
              />
            </UIFormItem>

            <UIFormItem
              v-if="editPayload"
              class="mt-[15px]"
              :label="$t('settings.calendar.added_calendars')"
              :required="true"
              :show-feedback="false"
            >
              <UISelect
                id="select-calendars"
                v-model:value="selectedCalendars"
                :options="calendarOptions"
                :max-tag-count="3"
                :multiple="true"
                :filterable="true"
                :clearable="true"
                :placeholder="$t('settings.calendar.select_calendars')"
              >
                <template #empty>
                  <div class="text-[14px] font-normal text-gray-500">
                    {{ $t('settings.calendar.no_calendars_found_title') }}
                  </div>
                </template>
              </UISelect>
            </UIFormItem>

            <div class="mb-2 mt-[20px]">
              <div class="mb-1 flex flex-row text-sm font-medium text-gray-700">
                {{ $t('settings.calendar.template') }}
              </div>
              <div class="mb-3 text-xs text-gray-500">
                {{ $t('settings.calendar.template_description') }}
              </div>
              <UISelect
                id="group-template"
                v-model:value="formValue.widgetType"
                :options="groupTemplateOptions"
                :placeholder="$t('settings.calendar.select_template')"
              />
            </div>
            <UIFormItem
              :label="$t('settings.calendar.group_url')"
              path="slug"
              :style="{
                marginTop: '20px',
              }"
            >
              <UIInputGroup>
                <UIInputGroupLabel>/widget/groups/</UIInputGroupLabel>
                <UIInput
                  id="calendarUrl"
                  v-model="formValue.slug"
                  :loading="isSlugValidating"
                  :placeholder="$t('settings.calendar.15mins')"
                />
              </UIInputGroup>
            </UIFormItem>
          </div>
        </UIModalContent>
      </UIForm>

      <template #footer>
        <UIModalFooter
          id="affiliateCreateModalFooter"
          :positive-text="
            props.editPayload
              ? $t('settings.calendar.save')
              : $t('settings.calendar.create')
          "
          :negative-text="$t('settings.calendar.cancel')"
          :loading="saving"
          :disabled="isSlugValidating || saving"
          @positive-click="handleValidateClick"
          @negative-click="$emit('closeModal')"
        />
      </template>
    </UIModal>

    <ConflictingCalendarsModal
      v-if="showConflictingCalendars"
      :show="showConflictingCalendars"
      :items="conflictingCalendarsTable"
      :group-name="formValue.name"
      :on-cancel="onCancelConflictModal"
      :on-confirm="onConfirmConflictModal"
    />
  </div>
</template>
